import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { ExternalLink } from 'lucide-react'

type LandingPageDemo = {
  title: string
  url: string
  description: string
  previewImage?: string
}

const demos: LandingPageDemo[] = [
  {
    title: 'Beauty Clinic Demo',
    url: 'https://demo-beauty.shejza.com',
    description:
      'Luxury aesthetic clinic landing page with elegant design and animations.',
    previewImage: '/landing-previews/beauty-clinic.png',
  },
  {
    title: 'Dental Clinic Demo',
    url: 'https://demo-dental.shejza.com',
    description:
      'Clean and modern dental clinic website focused on services and booking.',
    previewImage: '/landing-previews/dental-clinic.png',
  },
  {
    title: 'Auto Salon Demo',
    url: 'https://demo-autosalon.shejza.com',
    description:
      'Premium auto salon landing page focused on vehicle showcase and test-drive conversion.',
    previewImage: '/landing-previews/autosalon.png',
  },
  {
    title: 'Solar Energy Demo',
    url: 'https://demo-solar.shejza.com',
    description:
      'Modern website for a solar energy company showcasing services and projects.',
    previewImage: '/landing-previews/solar-energy.png',
  },
  {
    title: 'Construction Demo',
    url: 'https://demo-construction.shejza.com',
    description:
      'Professional construction company landing page with project showcase.',
    previewImage: '/landing-previews/construction.png',
  },
]

const createPreviewImages = (url: string) => {
  const encoded = encodeURIComponent(url)
  const normalized = url.replace(/^https?:\/\//, '')

  return [
    `https://image.thum.io/get/width/1200/crop/760/noanimate/https://${normalized}`,
    `https://image.thum.io/get/width/1200/crop/760/noanimate/${encoded}`,
    `https://s.wordpress.com/mshots/v1/${encoded}?w=1200`,
  ]
}

const LandingDemoCard = ({ demo, index }: { demo: LandingPageDemo; index: number }) => {
  const [previewIndex, setPreviewIndex] = useState(0)

  const previewImages = useMemo(() => {
    if (demo.previewImage) {
      return [demo.previewImage, ...createPreviewImages(demo.url)]
    }
    return createPreviewImages(demo.url)
  }, [demo.previewImage, demo.url])
  const imageFailed = previewIndex >= previewImages.length

  return (
    <motion.a
      href={demo.url}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 28 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="group block rounded-2xl border border-gray-200/70 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-custom-700/50 dark:bg-custom-800"
    >
      <div className="overflow-hidden rounded-t-2xl border-b border-gray-200/70 bg-gradient-to-br from-gray-50 to-gray-100 dark:border-custom-700/60 dark:from-custom-900 dark:to-custom-800">
        {imageFailed ? (
          <div className="h-64 md:h-72 flex items-center justify-center">
            <span className="text-sm tracking-wide text-gray-500 dark:text-gray-400">Preview unavailable</span>
          </div>
        ) : (
          <div className="h-64 md:h-72 p-2">
            <img
              src={previewImages[previewIndex]}
              alt={`${demo.title} preview`}
              loading="lazy"
              onError={() => setPreviewIndex((prev) => prev + 1)}
              className="h-full w-full rounded-xl object-contain transition-transform duration-500 group-hover:scale-[1.01]"
            />
          </div>
        )}
      </div>

      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{demo.title}</h3>
        <p className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-400">{demo.description}</p>

        <span className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary-50 px-4 py-2 text-sm font-medium text-primary-700 transition-colors duration-200 group-hover:bg-primary-100 dark:bg-primary-900/30 dark:text-primary-300 dark:group-hover:bg-primary-900/40">
          View Demo
          <ExternalLink className="h-4 w-4" />
        </span>
      </div>
    </motion.a>
  )
}

const LandingPages = () => {
  return (
    <section className="min-h-screen py-16">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white md:text-4xl">Landing Pages</h1>
          <p className="mt-4 max-w-3xl text-base text-gray-600 dark:text-gray-400 md:text-lg">
            A collection of modern landing pages created for different industries.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          {demos.map((demo, index) => (
            <LandingDemoCard key={demo.url} demo={demo} index={index} />
          ))}
        </div>
      </div>
    </section>
  )
}

export default LandingPages
